import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { getTrendIndex } from "../lib/api";
import { TrendingUp, TrendingDown, Users, Radio, Sparkles, Trophy, Crown, Swords, Award } from "lucide-react";
import useMarketSocket from "../hooks/useMarketSocket";
import { HeroAvatar } from "../components/Avatar";

export default function IndexDetail() {
  const { id } = useParams();
  const [idx, setIdx] = useState(null);
  const [pulse, setPulse] = useState(false);
  const { prices, connected } = useMarketSocket();

  useEffect(() => {
    getTrendIndex(id).then(setIdx);
  }, [id]);

  useEffect(() => {
    const u = prices[id];
    if (!u || !idx) return;
    setIdx((prev) => prev ? { ...prev, change_24h: u.change_24h, heat_score: u.heat_score, form_points: u.heat_score } : prev);
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, id]);

  if (!idx) return (
    <div className="min-h-screen pb-safe"><Header title="Star" />
      <div className="max-w-xl mx-auto px-4 pt-6 text-white/60">Loading…</div>
      <BottomNav />
    </div>
  );

  const up = idx.change_24h >= 0;
  const points = Number(idx.form_points ?? idx.heat_score ?? 0);
  const breakdown = idx.form_breakdown || [];
  const totalBreakdown = breakdown.reduce((s, b) => s + (b.points || 0), 0);

  return (
    <div className="min-h-screen pb-safe" data-testid="index-detail">
      <Header title="Star" />
      <div className="max-w-xl mx-auto">
        <div className="relative">
          <HeroAvatar name={idx.name} category={idx.category} symbol={idx.symbol} height={224} />
          <div className="absolute top-4 left-4">
            <span className="chip" style={{ background: connected ? "#00FF6622" : "#FFD60022", color: connected ? "#00FF66" : "#FFD600", border: `1px solid ${connected ? "#00FF6655" : "#FFD60055"}` }}>
              <Radio className="w-3 h-3" /> {connected ? "Live" : "…"}
            </span>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-xs uppercase tracking-widest text-white/75">@{idx.handle} · {idx.category}{idx.subtype ? ` · ${idx.subtype}` : ""}</p>
            <h1 className="font-display text-3xl font-bold">{idx.name}</h1>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-4">
          {/* Big fantasy-points hero */}
          <div className="glass-strong rounded-3xl p-5 relative overflow-hidden" data-testid="form-points-hero">
            <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-30"
              style={{ background: "radial-gradient(circle,#FFD600,transparent 70%)" }} />
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/55">Current form points</p>
            <div className="flex items-end gap-2 mt-1">
              <span className={`font-display text-6xl font-bold leading-none ${pulse ? "pop" : ""}`} style={{ color: "#FFD600" }} data-testid="form-points-value">
                {points}
              </span>
              <span className="text-sm text-white/55 font-mono-brand mb-1">/ 100 pts</span>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-[#00FF66]" : "text-[#FF0055]"}`}>
                {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                Form trend {up ? "+" : ""}{idx.change_24h?.toFixed(1)}%
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-white/55">
                <Users className="w-3.5 h-3.5" /> {idx.followers?.toLocaleString("en-IN")} fans
              </span>
            </div>
            {idx.bio && <p className="mt-3 text-sm text-white/70 leading-relaxed">{idx.bio}</p>}
          </div>

          {/* Form breakdown — Dream11 style */}
          <div className="glass rounded-3xl p-4" data-testid="form-breakdown-card">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#B026FF]" /> This week's form
              </h3>
              <span className="font-mono-brand text-xs text-white/55">Σ {totalBreakdown} pts</span>
            </div>
            <p className="text-[11px] text-white/45 mt-0.5">How this star is scoring right now — pick them in your Circle to bag these points.</p>
            <div className="mt-3 space-y-1.5">
              {breakdown.length === 0 && (
                <p className="text-xs text-white/45">Form data refreshes after the next contest cycle.</p>
              )}
              {breakdown.map((b, i) => {
                const positive = (b.points || 0) >= 0;
                return (
                  <div
                    key={`${b.label}-${i}`}
                    className="flex items-center gap-2 rounded-xl p-2.5"
                    style={{ background: positive ? "rgba(0,255,102,0.05)" : "rgba(255,0,85,0.06)", border: `1px solid ${positive ? "rgba(0,255,102,0.18)" : "rgba(255,0,85,0.22)"}` }}
                    data-testid={`breakdown-item-${i}`}
                  >
                    <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
                      style={{ background: positive ? "rgba(0,255,102,0.12)" : "rgba(255,0,85,0.16)", border: `1px solid ${positive ? "rgba(0,255,102,0.35)" : "rgba(255,0,85,0.45)"}` }}>
                      <Award className="w-3.5 h-3.5" style={{ color: positive ? "#00FF66" : "#FF0055" }} />
                    </span>
                    <p className="text-sm text-white/85 flex-1 truncate">{b.label}</p>
                    <span className={`font-mono-brand font-bold text-sm shrink-0 ${positive ? "text-[#00FF66]" : "text-[#FF0055]"}`}>
                      {positive ? "+" : ""}{b.points}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Mini label="Captain (2×)" value={`${(points * 2).toFixed(0)} pts`} color="#FFD600" />
              <Mini label="Vice (1.5×)" value={`${(points * 1.5).toFixed(0)} pts`} color="#00F0FF" />
              <Mini label="Regular pick" value={`${points} pts`} color="#fff" />
            </div>
          </div>

          {/* CTA card */}
          <div className="glass-strong rounded-2xl p-4">
            <p className="text-xs tracking-widest uppercase font-bold text-white/55">Use this star</p>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              <b className="text-white">Pick them as Captain</b> to earn {(points * 2).toFixed(0)} points if their form holds.
              No prices, no trading — just predict who's about to peak.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link to="/contests" data-testid="go-contests-cta" className="py-3 rounded-full font-bold text-center text-black inline-flex items-center justify-center gap-1"
                style={{ background: "linear-gradient(90deg,#FFD600,#FF0055)", boxShadow: "0 0 22px rgba(255,214,0,0.25)" }}>
                <Trophy className="w-4 h-4" /> Pick a Contest
              </Link>
              <Link to="/duels" data-testid="go-duels-cta" className="py-3 rounded-full font-bold text-center text-black inline-flex items-center justify-center gap-1"
                style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)", boxShadow: "0 0 22px rgba(0,240,255,0.25)" }}>
                <Swords className="w-4 h-4" /> Duel a Friend
              </Link>
            </div>
            <p className="text-[11px] text-white/40 mt-3">
              Fanpuff is free-to-play. No deposits, no cash prizes. Fanpuff Credits have no cash value.
            </p>
          </div>
        </div>

        <div className="h-10" />
      </div>
      <BottomNav />
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div className="rounded-xl p-2" style={{ background: `${color === "#fff" ? "rgba(255,255,255,0.05)" : `${color}12`}`, border: `1px solid ${color === "#fff" ? "rgba(255,255,255,0.08)" : `${color}33`}` }}>
      <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: color === "#fff" ? "rgba(255,255,255,0.55)" : color }}>{label}</p>
      <p className="font-display text-sm font-bold mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}
